import { useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
    fetchDownloadDicomSeries,
    useRegistrationTaskProcessing,
} from '~/backend/connector-api/connector-components';
import useProcessDicomFiles from '~/hooks/useProcessDicomFiles';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import { Job } from '~/state/atoms/jobAtoms';
import { selectedPatientIdState } from '~/state/atoms/patientAtoms';
import { workflowModalState, WorkflowTypes } from '~/state/atoms/viewerAtoms';
import { handleError } from '~/utils/errors';
import { PredictionModel } from '~/utils/transforms/predictionModelsDataTransforms';
import { transformTaskToGenerationModel } from '~/utils/transforms/taskTransforms';
import { NewJobData, useJobManagement } from '../../useJobManagement';
import useStructureSetCacheUpdate from '../useStructureSetCacheUpdate';

export type ConnectorContourPropagationTaskInput = {
    selectedModel?: PredictionModel;
    selectedStructures?: string[];
    selectedCustomization?: string;
    patientId?: string;
    sourceImageStudyUid?: string;
    sourceImageSeriesUid?: string;
    sourceStructureSetUid?: string;
    targetImageStudyUid?: string;
    targetImageSeriesUid?: string;
};

/**
 * A hook for creating a contour propagation task via Connector API.
 * @returns {Object} - An object containing the start function and submission state.
 */
export default function useContourPropagationTask() {
    const selectedPatientId = useRecoilValue(selectedPatientIdState);
    const setWorkflowModalState = useSetRecoilState(workflowModalState);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { mutateAsync: startRegistration } = useRegistrationTaskProcessing();
    const { processImportedFiles } = useProcessDicomFiles();
    const updateCachedStructureSet = useStructureSetCacheUpdate();
    const { addJob, updateJob } = useJobManagement();
    const { processCreatedFiles } = useProcessDicomFiles();
    const { setEnvironmentStructureSet } = useViewportEnvironment();

    const handleProcessedFiles = async (job: Job) => {
        if (job.results && job.results?.length > 0) {
            const generationModel = transformTaskToGenerationModel(job.task);
            const processedItems = await processCreatedFiles(
                job.results,
                generationModel
            );
            if (processedItems) {
                updateJob(job.id, { isImported: true });
                setEnvironmentStructureSet(
                    processedItems.structureSets,
                    selectedPatientId ?? ''
                );
            }
        }
    };

    /**
     * Creates the contour propagation task with the provided input.
     * @param {ConnectorContourPropagationTaskInput} input - The input for the task.
     */
    const start = async (input: ConnectorContourPropagationTaskInput) => {
        if (isSubmitting) {
            return;
        }

        if (!input.selectedModel) {
            handleError(new Error('No model selected.'));
            return;
        }
        if (!input.sourceImageSeriesUid || !input.targetImageSeriesUid) {
            handleError(
                new Error('Source or target image series UID missing.')
            );
            return;
        }
        if (!input.sourceImageStudyUid || !input.targetImageStudyUid) {
            handleError(new Error('Source or target image study UID missing.'));
            return;
        }

        setIsSubmitting(true);
        try {
            await updateCachedStructureSet();

            const modelIdentifier = input.selectedCustomization
                ? `${input.selectedModel.name}.${input.selectedCustomization}`
                : input.selectedModel.name;

            const task = await startRegistration({
                body: {
                    modelName: modelIdentifier,
                    sourceImageSeries: {
                        seriesInstanceUID: input.sourceImageSeriesUid,
                        studyInstanceUID: input.sourceImageStudyUid,
                    },
                    destinationImageSeries: {
                        seriesInstanceUID: input.targetImageSeriesUid,
                        studyInstanceUID: input.targetImageStudyUid,
                    },
                    structureSet: {
                        sopInstanceUID: input.sourceStructureSetUid,
                        patientID: input.patientId,
                    },
                },
            });

            const newJob: NewJobData = {
                name: `Adapt+`,
                id: task.id,
                type: WorkflowTypes.ADAPT,
                task: {
                    model: input.selectedModel,
                    output: task,
                },
                onLoad: handleProcessedFiles,
            };

            addJob(newJob);
            setWorkflowModalState((state) => ({
                ...state,
                open: false,
            }));

            // start loading the destination image series to UI
            fetchAndProcessTargetImage(
                input.targetImageStudyUid,
                input.targetImageSeriesUid
            );
        } catch (error) {
            handleError(error, 'Failed to start contour propagation task.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const fetchAndProcessTargetImage = async (
        imageStudyUid: string,
        imageSeriesUid: string
    ) => {
        try {
            const dicomBlob = await fetchDownloadDicomSeries({
                pathParams: {
                    studyId: imageStudyUid,
                    seriesId: imageSeriesUid,
                },
                queryParams: { series: true },
            });
            const files = [new File([dicomBlob], `${imageSeriesUid}.zip`)];

            await processImportedFiles(files);
        } catch (fetchError) {
            handleError(
                fetchError,
                `Couldn't process DICOM files for ${imageSeriesUid}.`
            );
        }
    };

    return { start, isSubmitting };
}
